import { useState } from 'react'
import { useEffect } from 'react'
import { Button } from '@/components/Button'
import { CategoryManager } from '@/components/CategoryManager'
import { ChangelogList } from '@/components/WhatsNew'
import { LISTED } from '@/data/changelog'
import { Sheet } from '@/components/Sheet'
import { APP_NAME, APP_VERSION } from '@/config'
import { addMonthsClamped, fmtDate, today } from '@/logic/dates'
import { Field } from '@/components/Field'
import { InstallSettings } from '@/components/InstallHint'
import { InviteManager } from '@/components/InviteManager'
import { isAdmin } from '@/data/invites'
import { NotificationSettings } from '@/components/NotificationSettings'
import { Segmented } from '@/components/Segmented'
import { Switch } from '@/components/Switch'
import { SourcePanel } from '@/components/SourcePanel'
import { checkConnection, useCalendar } from '@/store/calendar'
import { setMemberName, updateHousehold } from '@/data/household'
import { cleanUpBefore, updateSettings } from '@/data/user'
import { usePaths } from '@/hooks/usePaths'
import { signOutUser, useAuth } from '@/store/auth'
import { useCategories } from '@/store/categories'
import { useHousehold } from '@/store/household'
import { useTasks } from '@/store/tasks'
import { setFeedbackOpen, showToast } from '@/store/ui'
import type { ThemeSetting } from '@/types'
import './SettingsScreen.css'

/**
 * Settings: profile name, household and the other person's email, categories,
 * Google Calendar (read-only), notifications, theme, default visibility, the
 * history clean-up, and invite codes for an admin.
 *
 * Text fields are uncontrolled and keyed on the saved value, so a live update
 * from the other person resets them without an effect, and each saves when
 * focus leaves it.
 */
export function SettingsScreen() {
  const user = useAuth((s) => s.user)!
  const paths = usePaths()
  const hid = useHousehold((s) => s.hid)!
  const household = useHousehold((s) => s.household)!
  const settings = useHousehold((s) => s.userDoc?.settings)
  const startPrivate = (settings?.defaultVisibility ?? 'private') === 'private'
  const shared = useCategories((s) => s.shared)
  const priv = useCategories((s) => s.private)
  const isOwner = household.ownerUid === user.uid
  const myEmail = user.email
  const myName = household.members[user.uid]?.name ?? ''
  const otherEmail = household.memberEmails.find((e) => e !== myEmail) ?? ''
  const other = Object.entries(household.members).find(([uid]) => uid !== user.uid)
  const [cleaning, setCleaning] = useState(false)
  const [cleanAge, setCleanAge] = useState<'3' | '12'>('12')
  const [historyOpen, setHistoryOpen] = useState(false)
  const connection = useCalendar((s) => s.connection)
  const [admin, setAdmin] = useState(false)

  useEffect(() => {
    let live = true
    void isAdmin(user.uid).then((yes) => live && setAdmin(yes))
    return () => {
      live = false
    }
  }, [user.uid])

  useEffect(() => {
    if (connection === 'unknown') void checkConnection()
  }, [connection])

  const saveName = async (value: string) => {
    if (value.trim() && value.trim() !== myName) {
      await setMemberName(hid, user.uid, value)
      showToast('Name saved')
    }
  }

  const saveHouseholdName = async (value: string) => {
    if (value.trim() && value.trim() !== household.name) {
      await updateHousehold(hid, user.uid, household, { name: value })
      showToast('Household saved')
    }
  }

  const saveOtherEmail = async (value: string) => {
    if (value.trim().toLowerCase() !== otherEmail) {
      await updateHousehold(hid, user.uid, household, { otherEmail: value })
      showToast('Household saved')
    }
  }

  const cleanUp = async () => {
    setCleaning(true)
    try {
      const before = addMonthsClamped(today(), -Number(cleanAge))
      const open = useTasks.getState().all.filter((t) => t.kind !== 'once')
      const { tasks, ticks } = await cleanUpBefore(paths, before, open)
      const bits = [tasks ? `${tasks} done task${tasks === 1 ? '' : 's'}` : '', ticks ? `${ticks} old tick${ticks === 1 ? '' : 's'}` : ''].filter(Boolean)
      showToast(bits.length ? `Removed ${bits.join(' and ')}` : `Nothing from before ${fmtDate(before, true)}`)
    } finally {
      setCleaning(false)
    }
  }

  return (
    <div className="settings">
      <section className="settings__section">
        <h2 className="eyebrow">You</h2>
        <div className="settings__card">
          <Field key={myName} label="Your name" defaultValue={myName} onBlur={(e) => void saveName(e.target.value)} maxLength={40} />
          <p className="settings__line">Signed in as {myEmail}</p>
        </div>
      </section>

      <section className="settings__section">
        <h2 className="eyebrow">Household</h2>
        <div className="settings__card">
          {isOwner ? (
            <>
              <Field key={household.name} label="Household name" defaultValue={household.name} onBlur={(e) => void saveHouseholdName(e.target.value)} maxLength={60} />
              <Field
                key={otherEmail}
                label="The other person's Google email"
                type="email"
                inputMode="email"
                autoCapitalize="none"
                defaultValue={otherEmail}
                onBlur={(e) => void saveOtherEmail(e.target.value)}
                placeholder="name@gmail.com"
                hint={other ? `${other[1].name} has joined.` : 'They join automatically when they sign in with this address.'}
              />
            </>
          ) : (
            <>
              <p className="settings__line">
                <strong>{household.name}</strong>
              </p>
              {other && <p className="settings__line">With {other[1].name}.</p>}
            </>
          )}
          <p className="settings__line settings__line--muted">
            Members:{' '}
            {Object.values(household.members)
              .map((m) => m.name)
              .join(', ')}
          </p>
        </div>
      </section>

      <section className="settings__section">
        <h2 className="eyebrow">Categories</h2>
        <div className="settings__card">
          <CategoryManager scope="shared" categories={shared} />
        </div>
        <h3 className="settings__sub">Private categories</h3>
        <div className="settings__card">
          <p className="settings__line settings__line--muted">Only you see these. A private task may use either kind.</p>
          <CategoryManager scope="private" categories={priv} />
        </div>
      </section>

      <section className="settings__section">
        <h2 className="eyebrow">Install</h2>
        <div className="settings__card">
          <InstallSettings />
        </div>
      </section>

      <section className="settings__section">
        <h2 className="eyebrow">Google Calendar</h2>
        <div className="settings__card">
          <SourcePanel full />
        </div>
      </section>

      <section className="settings__section">
        <h2 className="eyebrow">Notifications</h2>
        <div className="settings__card">
          <NotificationSettings />
        </div>
      </section>

      <section className="settings__section">
        <h2 className="eyebrow">Appearance</h2>
        <div className="settings__card">
          <div className="settings__row">
            <span>Theme</span>
            <Segmented<ThemeSetting>
              label="Theme"
              value={settings?.theme ?? 'system'}
              options={[
                { value: 'system', label: 'System' },
                { value: 'light', label: 'Daylight' },
                { value: 'dark', label: 'Dusk' },
              ]}
              onChange={(theme) => void updateSettings(user.uid, { theme })}
            />
          </div>
        </div>
      </section>

      <section className="settings__section">
        <h2 className="eyebrow">Tasks</h2>
        <div className="settings__card">
          <div className="settings__row">
            <span className="settings__row-label">
              New tasks are for
              <span className="settings__hint">Who's doing a task you add, until you change it on the task.</span>
            </span>
            <Segmented<'either' | 'me'>
              label="New tasks are for"
              value={settings?.defaultAssignee ?? 'either'}
              options={[
                { value: 'either', label: 'Either of us' },
                { value: 'me', label: 'Me' },
              ]}
              onChange={(defaultAssignee) => void updateSettings(user.uid, { defaultAssignee })}
            />
          </div>
          <button
            type="button"
            className="settings__row settings__row--button"
            aria-pressed={startPrivate}
            onClick={() => void updateSettings(user.uid, { defaultVisibility: startPrivate ? 'shared' : 'private' })}
          >
            <span className="settings__row-label">
              New tasks start private
              <span className="settings__hint">Only you can see them. The lock on quick add starts from this.</span>
            </span>
            <Switch on={startPrivate} />
          </button>
          <div className="settings__row">
            <span className="settings__row-label">
              Making a private task shared
              <span className="settings__hint">Whether the task screen checks with you first.</span>
            </span>
            <Segmented<'ask' | 'share'>
              label="Making a private task shared"
              value={settings?.askBeforeSharing === false ? 'share' : 'ask'}
              options={[
                { value: 'ask', label: 'Ask first' },
                { value: 'share', label: 'Just share' },
              ]}
              onChange={(v) => void updateSettings(user.uid, { askBeforeSharing: v === 'ask' })}
            />
          </div>
          <div className="settings__row settings__row--wrap">
            <span className="settings__row-label">
              Clean up history
              <span className="settings__hint">What got done is kept for good, so past weeks stay complete. This removes done tasks, and the ticks of repeats and routines, from before the age you pick. Nothing else.</span>
            </span>
            <span className="settings__clean">
              <Segmented<'3' | '12'>
                label="Older than"
                value={cleanAge}
                options={[
                  { value: '3', label: '3 months' },
                  { value: '12', label: '1 year' },
                ]}
                onChange={setCleanAge}
              />
              <Button variant="secondary" onClick={() => void cleanUp()} disabled={cleaning}>
                Clean up
              </Button>
            </span>
          </div>
        </div>
      </section>

      <section className="settings__section">
        <h2 className="eyebrow">About</h2>
        <div className="settings__card">
          <div className="settings__row">
            <span className="settings__row-label">
              {APP_NAME} {APP_VERSION}
              {LISTED[0] && <span className="settings__hint">Updated {fmtDate(LISTED[0].date, true)}</span>}
            </span>
            <Button variant="secondary" onClick={() => setHistoryOpen(true)}>
              What's new
            </Button>
          </div>
          <div className="settings__row">
            <span className="settings__row-label">
              Send feedback
              <span className="settings__hint">Something broken, or an idea? It goes straight to the person who builds {APP_NAME}.</span>
            </span>
            <Button variant="secondary" onClick={() => setFeedbackOpen(true)}>
              Send feedback
            </Button>
          </div>
        </div>
        <Sheet open={historyOpen} heading="What's new" onClose={() => setHistoryOpen(false)}>
          <ChangelogList entries={LISTED} current={APP_VERSION} />
        </Sheet>
      </section>

      {admin && (
        <section className="settings__section">
          <h2 className="eyebrow">Invites</h2>
          <InviteManager />
        </section>
      )}

      <section className="settings__section">
        <h2 className="eyebrow">Account</h2>
        <div className="settings__card">
          <Button variant="secondary" onClick={() => void signOutUser()}>
            Sign out
          </Button>
        </div>
      </section>
    </div>
  )
}
